import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import config from "../config/environment";

function Courses() {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("All");

  // Handle filter change and save to session storage
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    sessionStorage.setItem('coursesFilter', newFilter);
  };
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const location = useLocation();



  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const snapshot = await getDocs(collection(db, "courses"));
        const coursesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCourses(coursesData);
      } catch (err) {
        setError("Failed to fetch courses");
        console.error("Error fetching courses:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, []);

  // Save and restore scroll position and filter state
  useEffect(() => {
    // Check if we're returning from a course details page
    const isReturningFromCourse = sessionStorage.getItem('returningFromCourse');
    
    if (isReturningFromCourse) {
      // Restore filter state
      const savedFilter = sessionStorage.getItem('coursesFilter');
      if (savedFilter) {
        setFilter(savedFilter);
      }
      
      // Restore scroll position with multiple attempts to ensure it works
      const savedScrollPosition = sessionStorage.getItem('coursesScrollPosition');
      if (savedScrollPosition) {
        const position = parseInt(savedScrollPosition);
        if (config.get('enableConsoleLogs')) {
          console.log('Restoring scroll position to:', position);
        }
        
        setIsRestoringScroll(true);
        
        // Multiple attempts to restore scroll position
        const restoreScroll = () => {
          window.scrollTo(0, position);
          document.documentElement.scrollTop = position;
          document.body.scrollTop = position;
        };
        
        // Immediate attempt
        restoreScroll();
        
        // Delayed attempts to catch any late DOM updates
        setTimeout(restoreScroll, 50);
        setTimeout(restoreScroll, 100);
        setTimeout(restoreScroll, 200);
        
        // Final attempt after all content is loaded
        setTimeout(() => {
          restoreScroll();
          setIsRestoringScroll(false);
        }, 300);
        
        setScrollPosition(position);
      }
      
      // Clear the flag
      sessionStorage.removeItem('returningFromCourse');
    } else {
      // New visit - scroll to top
      window.scrollTo(0, 0);
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    }
  }, []);

  // Listen for scroll events to continuously save position
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPosition = window.pageYOffset || 
                                   document.documentElement.scrollTop || 
                                   document.body.scrollTop || 
                                   0;
      setScrollPosition(currentScrollPosition);
      
      // Also save to session storage for immediate access
      sessionStorage.setItem('coursesScrollPosition', currentScrollPosition.toString());
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to save current state before navigating to course details
  const saveCurrentState = () => {
    // Save current filter
    sessionStorage.setItem('coursesFilter', filter);
    
    // Save current scroll position with multiple methods for accuracy
    const currentScrollPosition = window.pageYOffset || 
                                 document.documentElement.scrollTop || 
                                 document.body.scrollTop || 
                                 0;
    
    if (config.get('enableConsoleLogs')) {
      console.log('Saving scroll position:', currentScrollPosition);
    }
    sessionStorage.setItem('coursesScrollPosition', currentScrollPosition.toString());
    
    // Set flag that we're navigating to course details
    sessionStorage.setItem('returningFromCourse', 'true');
  };

  const prioritizedCourses = useMemo(() => {
    const priorityCodes = [
      "SECR1013",
      "SECR1213",
      "SECR2033",
      "SECR2242",
      "SECR2941",
      "SECR3242",
      "SECR3253",
      "SECR3941",
      "SECR4493",
      "SECJ1013",
      "SECJ1023",
      "SECJ2013",
      "SECJ2154",
      "SECJ2203",
      "SECJ3553",
      "SECI1013",
      "SECI1113",
      "SECI2143",
      "SECP1513",
    ];

    const categoryOrder = ["Networking & Security", "Software Engineering", "Others"];
    const filtered = filter === "All" ? courses : courses.filter(c => c.category === filter);

    return [...filtered]
      .sort((a, b) => {
        const aCodeIndex = priorityCodes.indexOf(a.code);
        const bCodeIndex = priorityCodes.indexOf(b.code);
        if (aCodeIndex !== -1 || bCodeIndex !== -1) {
          if (aCodeIndex === -1) return 1;
          if (bCodeIndex === -1) return -1;
          return aCodeIndex - bCodeIndex;
        }
        const aCat = categoryOrder.indexOf(a.category);
        const bCat = categoryOrder.indexOf(b.category);
        if (aCat !== bCat) return aCat - bCat;
        return (a.name || "").localeCompare(b.name || "");
      })
      .map(c => ({
        ...c,
        imageUrl: c.imageUrl || (c.code ? `/images/${c.code}.svg` : "/logo192.png"),
      }));
  }, [courses, filter]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center px-4">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Loading Courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Error Loading Courses</h2>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Scroll Restoration Indicator */}
      {isRestoringScroll && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg animate-pulse">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm font-medium">Restoring your position...</span>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 pb-16 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6 animate-fade-in-up leading-tight">
              My Academic Journey
            </h1>
            <p className="text-lg sm:text-xl text-blue-200 mb-6 sm:mb-8 animate-fade-in-up animation-delay-200 px-4">
              Browse categories and click to view detailed course information
            </p>
            <div className="max-w-4xl mx-auto">
              <div className="glass-dark rounded-2xl p-6 sm:p-8 animate-fade-in-up animation-delay-400">
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                  Here are all the courses I have taken at University. Passing them does not mean I am an expert, but I gained understanding. Courses related to Networking & Security are prioritized. I can understand Java code from OOP even if I am not an expert, and I grasp system design principles from System Analysis & Design. Each course provided practical knowledge applicable to my field of Computer Networks & Security.
                </p>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="mb-8 sm:mb-12 animate-fade-in-up animation-delay-600">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="text-center lg:text-left">
                <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">Filter by Category</h2>
                <p className="text-sm sm:text-base text-gray-300">Click on a category to filter courses</p>
              </div>
              <div className="glass-dark rounded-full p-2 w-full lg:w-auto">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {["All", "Networking & Security", "Software Engineering", "Others"].map(cat => (
                    <button
                      key={cat}
                      onClick={() => handleFilterChange(cat)}
                      className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-all duration-300 text-sm sm:text-base ${
                        filter === cat
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                          : "text-gray-300 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Course Count */}
            <div className="text-center">
              <p className="text-sm sm:text-base text-gray-300">
                Showing <span className="text-blue-400 font-semibold">{prioritizedCourses.length}</span> courses
                {filter !== "All" && ` in ${filter}`}
              </p>
              {/* Debug Info - Only show in development */}
              {config.get('showDebugInfo') && (
                <p className="text-xs text-gray-500 mt-2">
                  Scroll: {scrollPosition}px | Filter: {filter}
                </p>
              )}
            </div>
          </div>

          {/* Courses Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 animate-fade-in-up animation-delay-800">
            {prioritizedCourses.map((course, index) => (
              <div
                key={course.id}
                onClick={() => {
                  saveCurrentState();
                  window.location.href = `/courses/${course.id}`;
                }}
                className="group cursor-pointer"
              >
                <div className="glass-dark rounded-2xl p-4 sm:p-6 h-full card-hover cursor-hover transition-all duration-300">
                  {/* Course Image */}
                  <div className="relative mb-4 sm:mb-6">
                    <div className="w-full h-32 sm:h-48 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center overflow-hidden">
                      <img
                        src={course.imageUrl}
                        alt={course.name}
                        className="w-16 h-16 sm:w-24 sm:h-24 object-contain group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = "/logo192.png";
                        }}
                      />
                    </div>
                    {/* Category Badge */}
                    <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                      <span className="px-2 sm:px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs sm:text-sm font-medium backdrop-blur-sm">
                        {course.category}
                      </span>
                    </div>
                  </div>

                  {/* Course Info */}
                  <div className="space-y-2 sm:space-y-3">
                    <h3 className="text-lg sm:text-xl font-semibold text-white group-hover:text-blue-400 transition-colors duration-300 leading-tight">
                      {course.name}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-300">
                      <span className="font-medium text-blue-400">{course.code}</span>
                    </p>
                    <p className="text-xs sm:text-sm text-gray-400 line-clamp-3 leading-relaxed">
                      {course.description || "Course description not available."}
                    </p>
                    
                    {/* Course Details */}
                    <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-700/50">
                      <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                        <span className="text-gray-400">Credits:</span>
                        <span className="text-blue-400 font-semibold">{course.credits || "N/A"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${course.hasProject ? 'bg-green-500' : 'bg-gray-500'}`}></span>
                        <span className={`text-xs sm:text-sm font-medium ${course.hasProject ? 'text-green-400' : 'text-gray-400'}`}>
                          {course.hasProject ? 'Has Project' : 'No Project'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No Courses Message */}
          {prioritizedCourses.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-2xl font-semibold text-white mb-2">No courses found</h3>
              <p className="text-gray-300">Try selecting a different category or check back later.</p>
            </div>
          )}
        </div>
      </section>

      {/* Back to Home Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-slate-800/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">Ready to learn more about me?</h2>
          <p className="text-sm sm:text-base text-gray-300 mb-6 sm:mb-8 px-4">
            Explore my skills, experience, and projects to get a complete picture of my capabilities.
          </p>
          <Link
            to="/"
            className="btn-primary inline-flex items-center gap-2 cursor-hover text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
          >
            <span>Back to Home</span>
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Courses;